import { useLocation, Link } from 'react-router-dom';
import { useSidebar } from '@/contexts/SidebarContext';
import { Button } from '@/components/ui/button';
import { Menu, ChevronRight, Home } from 'lucide-react';
import { useMemo } from 'react';

// Map routes to labels
const routeLabels: Record<string, string> = {
  '/': 'Tableau de bord',
  '/schools': 'Écoles',
  '/school-networks': "Réseaux d'écoles",
  '/modules': 'Modules',
  '/notifications': 'Notifications',
  '/permissions': 'Permissions',
  '/roles': 'Roles',
  '/school-years': 'Années fiscales',
  '/billing': 'Factures',
  '/exports': 'Exports',
  '/white-labels': 'Marques blanches',
  '/login-photos': 'Photos du login',
  '/databases': 'Bases de donnees',
  '/migrations': 'Migrations',
  '/scripts': 'Scripts',
  '/search': 'Recherches',
  '/accounts': 'Comptes',
};

// Map routes to their parent group (for breadcrumb hierarchy)
const routeParents: Record<string, string> = {
  // Référentiels group
  '/modules': 'Référentiels',
  '/school-years': 'Référentiels',
  '/permissions': 'Référentiels',
  '/roles': 'Référentiels',
  // Outils group
  '/migrations': 'Outils',
  '/databases': 'Outils',
  '/exports': 'Outils',
  '/notifications': 'Outils',
  '/search': 'Outils',
  '/scripts': 'Outils',
  '/login-photos': 'Outils',
};

interface BreadcrumbItem {
  label: string;
  href: string;
  isLast: boolean;
}

export function Header() {
  const location = useLocation();
  const { toggleMobile, isCollapsed } = useSidebar();

  const breadcrumbs = useMemo((): BreadcrumbItem[] => {
    const paths = location.pathname.split('/').filter(Boolean);

    if (paths.length === 0) {
      return [{ label: 'Tableau de bord', href: '/', isLast: true }];
    }

    const items: BreadcrumbItem[] = [];
    let currentPath = '';

    paths.forEach((path, index) => {
      currentPath += `/${path}`;
      const label = routeLabels[currentPath] || path.charAt(0).toUpperCase() + path.slice(1);
      const isLast = index === paths.length - 1;

      // Add parent group if this is the first path segment and has a parent
      if (index === 0 && routeParents[currentPath]) {
        items.push({
          label: routeParents[currentPath],
          href: '#', // Parent group is not navigable
          isLast: false,
        });
      }

      items.push({
        label,
        href: currentPath,
        isLast,
      });
    });

    return items;
  }, [location.pathname]);

  return (
    <header className="sticky top-0 z-30 flex h-14 items-center gap-4 border-b border-gray-200 bg-white px-4">
      {/* Mobile menu button */}
      <Button
        variant="ghost"
        size="icon-sm"
        className="lg:hidden shrink-0"
        onClick={toggleMobile}
      >
        <Menu className="h-5 w-5" />
      </Button>

      {/* Breadcrumbs */}
      <nav className="flex items-center gap-1 text-sm overflow-x-auto">
        <Link
          to="/"
          className="flex items-center text-gray-500 hover:text-gray-900 transition-colors shrink-0"
        >
          <Home className="h-4 w-4" />
        </Link>

        {breadcrumbs.map((item, index) => (
          <div key={`${item.href}-${index}`} className="flex items-center gap-1 shrink-0">
            <ChevronRight className="h-4 w-4 text-gray-300" />
            {item.isLast ? (
              <span className="font-medium text-gray-900">{item.label}</span>
            ) : item.href === '#' ? (
              <span className="text-gray-500">{item.label}</span>
            ) : (
              <Link
                to={item.href}
                className="text-gray-500 hover:text-gray-900 transition-colors"
              >
                {item.label}
              </Link>
            )}
          </div>
        ))}
      </nav>
    </header>
  );
}
